import type { ReactElement } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button } from '#design-system/component/Button/Button'
import { Input } from '#design-system/component/Form/Input/Input'
import { Banner } from '#design-system/component/Layout/Banner/Banner'
import { getAuthRoute } from '~/common/utils/auth-routes'

import { AuthLayout } from './AuthLayout'

export const ForgotPassword = (): ReactElement => {
  const { t } = useTranslation()

  const [email, setEmail] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [isSuccess, setIsSuccess] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const handleSubmit = async (): Promise<void> => {
    setError(null)
    setIsSubmitting(true)

    try {
      const response = await fetch(getAuthRoute('reset-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ email }),
      })

      if (!response.ok) {
        const data = (await response.json().catch(() => ({}))) as { message?: string }
        throw new Error(data.message ?? t('auth.reset_password_failed'))
      }

      setIsSuccess(true)
    } catch (err) {
      const message = err instanceof Error ? err.message : t('auth.reset_password_failed')
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Show success state after email is sent
  if (isSuccess) {
    return (
      <AuthLayout
        subtitle={t('auth.reset_link_sent_body', { email })}
        title={t('auth.check_your_email')}
        type="forgot-password"
      >
        <p className="note">{t('auth.check_spam_folder')}</p>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout
      subtitle={t('auth.forgot_password_subtitle')}
      title={t('auth.forgot_your_password')}
      type="forgot-password"
    >
      {error && <Banner bodyText={error} headerText={t('auth.error')} semanticType="Error" />}

      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
      >
        <Input
          autoComplete="email"
          label={t('auth.email')}
          onChange={setEmail}
          onLiveChange={setEmail}
          placeholder={t('auth.email_placeholder')}
          required
          size="full"
          type="email"
          value={email}
        />

        <Button
          actionRole="primary"
          disabledMessage={isSubmitting ? t('auth.sending') : undefined}
          label={t('auth.send_reset_link')}
          type="submit"
        />
      </form>
    </AuthLayout>
  )
}
